import {
  renderActivityLabel,
  type ActivityLabel,
  type ActivityLabelArg,
} from "@/lib/activity-label";
import type { AgentPresentationStep } from "@/lib/agent-presentation-types";
import { shortcutKeyCap } from "@/lib/shortcuts";
import { shortcutPlatform } from "@/lib/shortcut-registry";

// desktop_target_labels.MAX_NAME_CHARS in the backend.
const MAX_NAME_CHARS = 24;

const MODIFIERS: Record<string, string> = {
  cmd: "Meta",
  command: "Meta",
  meta: "Meta",
  super: "Meta",
  win: "Meta",
  windows: "Meta",
  ctrl: "Control",
  control: "Control",
  alt: "Alt",
  opt: "Alt",
  option: "Alt",
  shift: "Shift",
};

/**
 * What an operate step is doing while it runs, for a step the agent gave no
 * words of its own: "Pressing ⌘N", "Scrolling down", "Double-clicking", in
 * the app language (the backend's desktop labels where they say the same).
 * Guide steps (point, highlight, draw, frame) stay silent without them.
 * Typed and pasted text is never read - it can hold a password.
 */
export function operateStepLabel(
  step: AgentPresentationStep,
): string | undefined {
  const label = operateStepActivityLabel(step);
  return label && renderActivityLabel(label, "");
}

function operateStepActivityLabel(
  step: AgentPresentationStep,
): ActivityLabel | undefined {
  switch (step.kind) {
    case "click":
      return step.app_command
        ? { key: "ui.choosing_menu_command" }
        : { key: clickKey(step.click_count, step.button) };
    case "hover":
      return { key: "desktop.pointing" };
    case "paste":
      return {
        key: step.replace ? "ui.replacing_text" : "desktop.pasting_text",
      };
    case "type":
      return { key: "ui.typing" };
    case "key":
      return {
        key: "desktop.pressing_named",
        args: { target: keyTarget(step.keys, step.repeat) },
      };
    case "scroll":
      return { key: `desktop.scrolling_${step.direction}` };
    case "drag":
      return { key: "desktop.dragging" };
    default:
      return undefined;
  }
}

function clickKey(count?: 1 | 2, button?: "left" | "right"): string {
  if (count === 2) {
    return button === "right"
      ? "desktop.double_right_clicking"
      : "desktop.double_clicking";
  }
  return button === "right" ? "desktop.right_clicking" : "desktop.clicking";
}

/** The pressed keys, with their repeat count: "⌘N", "Return ×3". */
function keyTarget(keys: string, repeat?: number): ActivityLabelArg {
  const key = keysLabel(keys) ?? { key: "ui.a_key" };
  return repeat !== undefined && repeat > 1
    ? { key: "desktop.key_repeat", args: { key, repeat } }
    : key;
}

/** "Cmd+Shift+N" as the keyboard shows it: ⌘⇧N on macOS, Ctrl+Shift+N on
 *  Windows. */
function keysLabel(keys: string): string | undefined {
  const caps = keys
    .split("+")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const modifier = MODIFIERS[part.toLowerCase()];
      if (modifier) return shortcutKeyCap(modifier);
      return part.length === 1
        ? part.toUpperCase()
        : part.charAt(0).toUpperCase() + part.slice(1);
    });
  return (
    clipName(caps.join(shortcutPlatform() === "macos" ? "" : "+")) || undefined
  );
}

/** Clipped as the backend's step labels clip a name: whitespace collapsed,
 *  at most 24 characters, the last one an ellipsis. */
function clipName(text: string): string {
  const chars = Array.from(text.split(/\s+/).filter(Boolean).join(" "));
  if (chars.length <= MAX_NAME_CHARS) return chars.join("");
  return (
    chars
      .slice(0, MAX_NAME_CHARS - 1)
      .join("")
      .trimEnd() + "…"
  );
}
